import {
  Grid,
  Paper,
  List,
  ListItem,
  Typography,
  IconButton,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import Link from '@mui/material/Link';
import SystemModuleList from './SystemModuleList';
import semver from 'semver';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import packageInfo from '../../../package.json';
import { useIsAdmin } from 'features/auth/hooks/useIsAdmin';
import { Edit } from '@mui/icons-material';
import PurgeSettingsDialog from './PurgeSettingsDialog';
import { requestUsers } from 'features/spectrasWebSocketServer/spectrasWebSocketServerSlice';

import useInterval from 'hooks/useInterval';
import { useUsers } from 'features/userManagement/api/getUsers';
import { useSwitches } from 'features/switches/api/getSwitches';
import { useLocations } from 'features/locations/api/getLocations';
import { usePurgeSettings } from './api/getPurgeSettings';
import { useSystemModules } from 'features/systemModules/api/getSystemModules';
import { useSystemModuleLimits } from 'features/systemModules/api/getModuleLimits';
import { useVersion } from './api/getVersion';
import ConnectionStatus from 'features/spectrasWebSocketServer/ConnectionStatus';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';
import { formatInTimeZone } from 'date-fns-tz';

const SystemInfoWidgets = () => {
  let version = packageInfo.version;
  const { dateFormat, timezone } = useUserDateTime();
  let buildDate = formatInTimeZone(
    new Date(packageInfo.buildDate),
    timezone,
    dateFormat
  );
  const user = useAppSelector((state) => state.auth.user);
  const connectedUserIds = useAppSelector(
    (state) => state.spectrasWebSocketServer.connectedUsers
  );
  const { data: users } = useUsers({});
  const { data: switches } = useSwitches({});
  const { data: purgeSettings } = usePurgeSettings({});
  const connectedUsers = users?.filter((user) =>
    connectedUserIds.includes(user.id)
  );
  const isAdmin = useIsAdmin();
  const dispatch = useAppDispatch();
  const { data: currentVersion } = useVersion({});
  const [versionError, setVersionFlag] = useState(false);
  const [purgeSettingsOpen, setPurgeSettingsOpen] = useState(false);
  const { data: modules } = useSystemModules({});
  const { data: moduleLimits } = useSystemModuleLimits({});
  const { data: locations } = useLocations({});
  const numberOfEnabledLocations = locations
    ? locations?.reduce((sum, item) => (item.enabled ? sum + 1 : sum), 0)
    : 0;
  const numberOfEnabledSwitches =
    switches?.reduce((sum, item) => (item.enabled ? sum + 1 : sum), 0) || 0;
  useEffect(() => {
    if (version && currentVersion) {
      const validation = semver.lt(version, currentVersion);
      setVersionFlag(validation);
    }
  }, [version, currentVersion]);

  const fetchActiveUsers = useCallback(async () => {
    if (!isAdmin) return;
    if (user) dispatch(requestUsers({ user_id: user.id }));
  }, [isAdmin, dispatch, user]);

  useEffect(() => {
    fetchActiveUsers();
  }, [dispatch, fetchActiveUsers]);

  useInterval(fetchActiveUsers, 60000);

  return (
    <Grid item container>
      <Grid
        item
        container
        style={{ width: '20vw', margin: 0, minWidth: '15rem' }}
        component={Paper}
      >
        <Grid item>
          <List>
            <ListItem>
              <Typography variant='h5'>Version</Typography>
            </ListItem>
            <ListItem>Build Version: {version}</ListItem>
            {versionError === true && (
              <ListItem>
                <Typography variant='subtitle1' color='secondary'>
                  <Link
                    target='_blank'
                    href='https://portal.lptapps.com/'
                    color='inherit'
                  >
                    <b>New Version Available: {currentVersion}</b>
                  </Link>
                </Typography>
              </ListItem>
            )}
            <ListItem>Build Date: {buildDate}</ListItem>
          </List>
        </Grid>
      </Grid>
      {(modules?.SWITCHES || modules?.ADVANCED_HISTORY || modules?.WEATHER) && (
        <Grid
          item
          container
          style={{
            width: '20vw',
            margin: 0,
            marginLeft: '1.5rem',
            minWidth: '15rem',
          }}
          component={Paper}
        >
          <Grid item>
            <List>
              <ListItem>
                <Typography variant='h5'>System Modules</Typography>
              </ListItem>
              <ListItem>
                <SystemModuleList />
              </ListItem>
              {modules?.SWITCHES && (
                <ListItem>
                  {numberOfEnabledSwitches} of {moduleLimits?.SWITCHES.limit}{' '}
                  Switches Enabled
                </ListItem>
              )}
              {modules?.WEATHER && (
                <ListItem>
                  {numberOfEnabledLocations} of {moduleLimits?.WEATHER.limit}{' '}
                  Weather Locations Enabled
                </ListItem>
              )}
            </List>
          </Grid>
        </Grid>
      )}
      {isAdmin && (
        <Grid
          item
          container
          style={{
            width: '20vw',
            margin: 0,
            marginLeft: '1.5rem',
            minWidth: '15rem',
          }}
          component={Paper}
        >
          <Grid item>
            <List>
              <ListItem>
                <Typography variant='h5'>Server Info</Typography>
              </ListItem>
              <ListItem>
                <ConnectionStatus />
              </ListItem>
              <ListItem>
                <Typography variant='subtitle2'>
                  Purging{' '}
                  {purgeSettings?.purge_enabled
                    ? `Enabled (${
                        purgeSettings?.purge_interval[0] === '1'
                          ? purgeSettings.purge_interval.slice(0, -1)
                          : purgeSettings.purge_interval
                      })`
                    : 'Disabled'}
                </Typography>
                <IconButton
                  sx={{ ml: '.1rem' }}
                  onClick={() => setPurgeSettingsOpen(true)}
                >
                  <Edit />
                </IconButton>
              </ListItem>
              <ListItem>
                <Grid container direction='column'>
                  <Typography variant='subtitle2' sx={{ minWidth: '6rem' }}>
                    Active Users:
                  </Typography>
                  {connectedUsers?.map((user) => (
                    <Grid item sx={{ pl: '2rem' }} key={user.id}>
                      <Typography variant='subtitle2'>{user.name}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </ListItem>
            </List>
          </Grid>
        </Grid>
      )}
      <PurgeSettingsDialog
        open={purgeSettingsOpen}
        settings={purgeSettings}
        handleClose={() => {
          setPurgeSettingsOpen(false);
        }}
      />
    </Grid>
  );
};

export default SystemInfoWidgets;
